import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';

const FIELD_TYPES = ['text','email','tel','select','radio','checkbox','textarea','date','number','file'];

export function Forms({ toast }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [embedFor, setEmbedFor] = useState(null);
  const [error, setError] = useState(null);

  // Always re-fetch on focus so a transient first-load failure doesn't strand
  // the screen blank — we used to require a hard refresh to recover.
  const load = () => {
    setLoading(true);
    setError(null);
    return api('/api/forms')
      .then(data => setForms(Array.isArray(data) ? data : []))
      .catch(e => { setError(e.message); toast(e.message, 'error'); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) load(); });
    return () => window.removeEventListener('tradeops:tab-focus', onFocus);
  }, []);

  const remove = async f => {
    if (!confirm(`Delete form "${f.name}"?`)) return;
    try { await api(`/api/forms/${f.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  const viewSubmissions = async f => {
    try { const subs = await api(`/api/forms/${f.id}/submissions`); setSubmissions({ form: f, subs }); }
    catch(e) { toast(e.message,'error'); }
  };

  const createSmsOptIn = async () => {
    try {
      const f = await api('/api/forms/create-sms-optin', { method:'POST' });
      load();
      toast(`✓ Created "${f.name}" — open it to copy the public URL into your TFV submission`);
    } catch(e) { toast(e.message, 'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:10, flexWrap:'wrap' }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Forms</h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="primary" onClick={createSmsOptIn}>📱 + SMS Opt-In (compliant)</Btn>
        <Btn onClick={()=>setEditing({ type:'lead', autoCreateContact:true, brandColor:'#8b5cf6' })}>+ New Form</Btn>
      </div>
    </div>

    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.6 }}>
      📝 Each form has a public URL you can drop on a landing page, in an SMS, or share directly. Submissions auto-create contacts and trigger the assigned workflow.
      <br/><strong style={{ color:G.text }}>SMS Opt-In forms</strong> ship with the full CTIA consent block + hosted Privacy Policy + SMS Terms — paste the public URL into your Twilio Toll-Free Verification as the opt-in screenshot.
    </div>

    {error && !loading && (
      <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.red+'66', background:G.red+'18', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
        <div style={{ fontSize:13 }}>⚠️ Couldn't load forms: {error}</div>
        <Btn sz="sm" v="primary" onClick={load}>Retry</Btn>
      </div>
    )}
    {!loading && !error && forms.length === 0 && (
      <div style={{ ...styles.card, padding:24, textAlign:'center', color:G.textMuted, fontSize:13, marginBottom:14 }}>
        No forms yet. Click <strong style={{ color:G.text }}>+ New Form</strong> or <strong style={{ color:G.text }}>+ SMS Opt-In</strong> to create your first.
        <div style={{ marginTop:10 }}><Btn sz="sm" onClick={load}>↻ Refresh</Btn></div>
      </div>
    )}
    {loading ? <Spinner/> : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
      {forms.map(f => (
        <div key={f.id} style={styles.card}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <strong>{f.name}</strong>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{f.headline}</div>
            </div>
            <Badge c={f.status==='active'?G.green:G.textMuted} bg={(f.status==='active'?G.green:G.textMuted)+'22'}>{f.status}</Badge>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:10, fontSize:12, color:G.textMuted }}>
            <span>📊 {f.submissionsCount} submissions</span>
            <span>·</span>
            <span>{f.fields.length} fields</span>
          </div>
          <div style={{ ...styles.card, padding:8, marginTop:10, background:G.input, fontSize:12, fontFamily:'monospace', wordBreak:'break-all' }}>
            <a href={f.publicUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan, textDecoration:'none' }}>{f.publicUrl}</a>
          </div>
          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            <Btn sz="sm" onClick={()=>setEditing(f)}>Edit</Btn>
            <Btn sz="sm" v="cyan" onClick={()=>viewSubmissions(f)}>📊 Submissions</Btn>
            <Btn sz="sm" onClick={()=>{ navigator.clipboard.writeText(f.publicUrl); toast('Copied'); }}>📋 URL</Btn>
            <Btn sz="sm" onClick={()=>setEmbedFor(f)}>📦 Embed</Btn>
            <Btn sz="sm" v="danger" onClick={()=>remove(f)}>×</Btn>
          </div>
        </div>
      ))}
      {forms.length === 0 && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted, gridColumn:'1/-1' }}>No forms yet. Create one to start capturing leads.</div>}
    </div>}

    <FormBuilderModal form={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    <SubmissionsModal data={submissions} onClose={()=>setSubmissions(null)}/>
    <EmbedModal form={embedFor} onClose={()=>setEmbedFor(null)} toast={toast}/>
  </div>;
}

function EmbedModal({ form, onClose, toast }) {
  if (!form) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const publicUrl = form.publicUrl;
  const privacyUrl = `${origin}/portal/legal/${form.slug}/privacy`;
  const termsUrl   = `${origin}/portal/legal/${form.slug}/terms`;
  const iframe = `<iframe src="${publicUrl}" width="100%" height="780" frameborder="0" style="border:none;max-width:600px;display:block;margin:0 auto"></iframe>`;
  const link   = `<a href="${publicUrl}">${form.type==='sms_optin'?'Sign up for SMS updates':form.headline||form.name}</a>`;
  const copy = (s) => { navigator.clipboard.writeText(s); toast('Copied'); };
  return <Modal open onClose={onClose} width={720}>
    <h2 style={{ margin:'0 0 6px 0' }}>📦 Embed: {form.name}</h2>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Drop into any website. The hosted version handles compliance (consent block, privacy, terms, STOP/HELP) automatically.</div>

    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:4 }}>Public URL (share / paste anywhere)</div>
      <div style={{ ...styles.card, padding:10, background:G.input, fontFamily:'monospace', fontSize:12, wordBreak:'break-all', display:'flex', gap:8, alignItems:'center' }}>
        <span style={{ flex:1, color:G.cyan }}>{publicUrl}</span>
        <Btn sz="xs" onClick={()=>copy(publicUrl)}>Copy</Btn>
      </div>
    </div>

    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:4 }}>Iframe snippet (paste into HTML / WordPress block / website builder)</div>
      <div style={{ ...styles.card, padding:10, background:G.input, fontFamily:'monospace', fontSize:11, wordBreak:'break-all' }}>
        {iframe}
      </div>
      <Btn sz="sm" onClick={()=>copy(iframe)} style={{ marginTop:6 }}>Copy iframe</Btn>
    </div>

    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:4 }}>Plain link (for SMS / email / button)</div>
      <div style={{ ...styles.card, padding:10, background:G.input, fontFamily:'monospace', fontSize:12, wordBreak:'break-all' }}>{link}</div>
      <Btn sz="sm" onClick={()=>copy(link)} style={{ marginTop:6 }}>Copy link HTML</Btn>
    </div>

    {form.type === 'sms_optin' && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.purple+'66', background:G.purpleDim }}>
      <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>🛡️ Twilio Toll-Free Verification — paste these into your submission</div>
      <div style={{ fontSize:12, lineHeight:1.7 }}>
        <div><strong>Opt-in flow URL:</strong> <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>{publicUrl}</code> <Btn sz="xs" onClick={()=>copy(publicUrl)}>Copy</Btn></div>
        <div style={{ marginTop:4 }}><strong>Privacy Policy:</strong> <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>{privacyUrl}</code> <Btn sz="xs" onClick={()=>copy(privacyUrl)}>Copy</Btn></div>
        <div style={{ marginTop:4 }}><strong>SMS Terms:</strong> <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>{termsUrl}</code> <Btn sz="xs" onClick={()=>copy(termsUrl)}>Copy</Btn></div>
        <div style={{ marginTop:8, fontSize:11, color:G.textMuted }}>Take a screenshot of the public URL showing the checkbox + consent text. That screenshot URL goes in the "Opt-In Screenshot" field on the TFV submission (Onboarding → Step 6).</div>
      </div>
    </div>}

    <div style={{ display:'flex', justifyContent:'flex-end' }}>
      <Btn onClick={onClose}>Done</Btn>
    </div>
  </Modal>;
}

function FormBuilderModal({ form, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [reviewsPreview, setReviewsPreview] = useState(null);
  useEffect(() => { setF(form||{}); setReviewsPreview(null); }, [form]);
  if (!form) return null;
  const isNew = !f.id;
  const fields = f.fields || [];

  const updateField = (i, k, v) => {
    const nf = fields.slice();
    nf[i] = { ...nf[i], [k]: v };
    setF({ ...f, fields: nf });
  };
  const addField = () => setF({ ...f, fields: [...fields, { id:`f${Date.now()}`, label:'New Field', type:'text', required:false }] });
  const delField = i => setF({ ...f, fields: fields.filter((_,j)=>j!==i) });

  const save = async () => {
    if (!f.name) return alert('Form name required');
    try {
      if (isNew) await api('/api/forms', { method:'POST', body: f });
      else await api(`/api/forms/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  const loadReviewsPreview = async () => {
    if (!f.id) return;
    try {
      const r = await api(`/api/forms/${f.id}/reviews?max=${f.reviewsCount||3}`);
      setReviewsPreview(r);
    } catch(e) { setReviewsPreview({ error: e.message, reviews:[] }); }
  };

  return <Modal open onClose={onClose} width={820}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Form':f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Form Name *"><Input value={f.name||''} onChange={e=>setF({...f,name:e.target.value})}/></Field>
      <Field label="Type">
        <Select value={f.type||'lead'} onChange={e=>setF({...f,type:e.target.value})}>
          <option value="lead">lead</option>
          <option value="booking">booking</option>
          <option value="quote_request">quote_request</option>
          <option value="survey">survey</option>
          <option value="sms_optin">sms_optin (compliant for A2P/TFV)</option>
        </Select>
      </Field>
      <Field label="Headline" style={{ gridColumn:'1/-1' }}><Input value={f.headline||''} onChange={e=>setF({...f,headline:e.target.value})}/></Field>
      <Field label="Subhead" style={{ gridColumn:'1/-1' }}><Input value={f.subhead||''} onChange={e=>setF({...f,subhead:e.target.value})}/></Field>
      <Field label="Submit Button Label"><Input value={f.submitLabel||'Submit'} onChange={e=>setF({...f,submitLabel:e.target.value})}/></Field>
      <Field label="Brand Color"><Input type="color" value={f.brandColor||'#8b5cf6'} onChange={e=>setF({...f,brandColor:e.target.value})} style={{ height:42, padding:4 }}/></Field>
      <Field label="Success Message" style={{ gridColumn:'1/-1' }}><Input value={f.successMessage||''} onChange={e=>setF({...f,successMessage:e.target.value})}/></Field>
    </div>

    {/* SMS Opt-In configurator — only shows when type is sms_optin */}
    {f.type === 'sms_optin' && <div style={{ ...styles.card, padding:14, marginTop:14, borderColor:G.purple+'66', background:G.purpleDim }}>
      <div style={{ fontWeight:700, marginBottom:6 }}>📱 SMS Opt-In Form Settings</div>
      <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.55 }}>
        This form auto-renders a CTIA-compliant consent block on submit — including: program description, your business name, message frequency disclosure, "msg & data rates may apply", STOP/HELP language, "consent not a condition of purchase", and links to your hosted Privacy Policy + SMS Terms.
        A required checkbox gates submission server-side.
      </div>
      <div style={{ marginTop:8, fontSize:12 }}>
        <div>✓ Privacy policy: <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>/portal/legal/{f.slug}/privacy</code></div>
        <div>✓ SMS terms: <code style={{ background:G.input, padding:'1px 6px', borderRadius:4 }}>/portal/legal/{f.slug}/terms</code></div>
        <div style={{ marginTop:6, color:G.textMuted }}>Use the 📦 Embed button (after saving) to grab the Twilio TFV-ready URLs.</div>
      </div>
    </div>}

    {/* Google Reviews widget configurator */}
    <div style={{ ...styles.card, padding:14, marginTop:14, borderColor:G.cyan+'66' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <input type="checkbox" id="reviews-on" checked={!!f.showReviewsWidget} onChange={e=>setF({...f, showReviewsWidget: e.target.checked})} style={{ width:18, height:18 }}/>
        <label htmlFor="reviews-on" style={{ fontWeight:700, cursor:'pointer' }}>📣 Show Google Reviews widget on this form</label>
      </div>
      <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.55, marginBottom:10 }}>
        Pulls the most-recent reviews + star rating from your Google Business Profile. Requires <strong style={{color:G.text}}>Google Place ID</strong> set (Onboarding → Step 4: Google Review Link, or Settings → Reputation).
        Cached 12h to avoid burning API quota. Renders above the form fields as social proof.
      </div>
      {f.showReviewsWidget && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="How many reviews to show (1–5)" style={{ marginBottom:0 }}>
          <Select value={f.reviewsCount||3} onChange={e=>setF({...f, reviewsCount: Number(e.target.value)})}>
            <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option><option value={5}>5</option>
          </Select>
        </Field>
        <Field label="Position" style={{ marginBottom:0 }}>
          <Select value={f.reviewsPosition||'above'} onChange={e=>setF({...f, reviewsPosition: e.target.value})}>
            <option value="above">Above the form</option>
            <option value="below">Below the form</option>
          </Select>
        </Field>
      </div>}
      {f.showReviewsWidget && f.id && <div style={{ marginTop:10 }}>
        <Btn sz="sm" onClick={loadReviewsPreview}>👁 Preview reviews</Btn>
        {reviewsPreview && !reviewsPreview.configured && <div style={{ marginTop:8, padding:10, background:G.yellowLight, color:G.yellow, fontSize:12, borderRadius:6 }}>
          ⚠️ No Google Place ID set on this account. Open Settings → Reputation and paste your Google Business Profile place ID (find it at <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" style={{ color:G.cyan }}>Google's Place ID Finder</a>).
        </div>}
        {reviewsPreview && reviewsPreview.error && <div style={{ marginTop:8, padding:10, background:G.redLight, color:G.red, fontSize:12, borderRadius:6 }}>{reviewsPreview.error}</div>}
        {reviewsPreview && reviewsPreview.reviews?.length > 0 && <div style={{ marginTop:8 }}>
          <div style={{ fontSize:13, fontWeight:600 }}>⭐ {reviewsPreview.rating} · {reviewsPreview.total} reviews</div>
          {reviewsPreview.reviews.map((r,i) => <div key={i} style={{ ...styles.card, padding:10, marginTop:6, fontSize:12 }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <strong>{r.author}</strong>
              <span style={{ color:G.yellow }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</span>
              <span style={{ color:G.textMuted, fontSize:11 }}>· {r.relativeTime}</span>
            </div>
            <div style={{ marginTop:4, color:G.textMuted }}>{r.text?.slice(0,200)}{r.text?.length>200?'…':''}</div>
          </div>)}
        </div>}
      </div>}
    </div>

    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Fields</div>
        <Btn sz="sm" onClick={addField}>+ Add Field</Btn>
      </div>
      {fields.map((field, i) => (
        <div key={i} style={{ ...styles.card, padding:12, marginBottom:8 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 120px auto auto', gap:8, alignItems:'flex-end' }}>
            <Field label="Label" style={{ marginBottom:0 }}><Input value={field.label||''} onChange={e=>updateField(i,'label',e.target.value)}/></Field>
            <Field label="ID (machine name)" style={{ marginBottom:0 }}><Input value={field.id||''} onChange={e=>updateField(i,'id',e.target.value)}/></Field>
            <Field label="Type" style={{ marginBottom:0 }}><Select value={field.type||'text'} onChange={e=>updateField(i,'type',e.target.value)}>{FIELD_TYPES.map(t => <option key={t}>{t}</option>)}</Select></Field>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, paddingBottom:10 }}>
              <input type="checkbox" checked={!!field.required} onChange={e=>updateField(i,'required',e.target.checked)}/>Required
            </label>
            <Btn sz="xs" v="danger" onClick={()=>delField(i)} style={{ marginBottom:10 }}>×</Btn>
          </div>
          {(field.type === 'select' || field.type === 'radio') && (
            <Field label="Options (comma-separated)" style={{ marginTop:8, marginBottom:0 }}>
              <Input value={(field.options||[]).join(', ')} onChange={e=>updateField(i,'options', e.target.value.split(',').map(s=>s.trim()).filter(Boolean))}/>
            </Field>
          )}
        </div>
      ))}
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save Form</Btn>
    </div>
  </Modal>;
}

function SubmissionsModal({ data, onClose }) {
  if (!data) return null;
  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 14px 0' }}>Submissions: {data.form.name}</h2>
    {data.subs.length === 0 ? <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No submissions yet.</div>
    : <div style={{ maxHeight:'70vh', overflow:'auto' }}>
      {data.subs.map(s => (
        <div key={s.id} style={{ ...styles.card, padding:14, marginBottom:8 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
            <strong>{s.contactName || 'Anonymous'}</strong>
            <span style={{ fontSize:12, color:G.textMuted }}>{new Date(s.submittedAt).toLocaleString()}</span>
          </div>
          <div style={{ ...styles.card, background:G.input, padding:10, fontSize:13, fontFamily:'monospace' }}>
            {Object.entries(s.data).map(([k,v]) => <div key={k}><strong>{k}:</strong> {String(v)}</div>)}
          </div>
        </div>
      ))}
    </div>}
    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}><Btn onClick={onClose}>Close</Btn></div>
  </Modal>;
}
